/**
 * Shared Pencil design tokens for platform-admin module screens
 * (permission-catalog, users directory), applied via inline styles.
 *
 * The STRUCTURAL tokens (surfaces, borders, primary/secondary text) map to the
 * design-system CSS theme variables (`var(--color-*)`) so these screens follow
 * the active platform-admin theme (light / dark / high-contrast) instead of
 * being locked to the dark Pencil palette. The dark-theme values of those
 * variables match the original Pencil hexes, so DARK MODE is visually unchanged;
 * this is what fixes the previous light-mode breakage (dark panels on a light
 * shell). See `packages/design-system/src/css/tokens.css`.
 *
 * The SATURATED accents (brand blues, status, chart purple, chip text) stay as
 * fixed Pencil hexes on purpose: they read on both light and dark surfaces, and
 * remapping them would risk inverting chip/pill contrast.
 *
 * DRY: this is the single source of truth. Feature-local `tokens.ts` files
 * re-export from here so their many `../tokens` imports keep working.
 */
export const COLORS = {
  // Surfaces — theme-aware (flip with light/dark; dark values == Pencil hexes).
  bg: 'var(--color-background)',
  surface: 'var(--color-surface)',
  surface2: 'var(--color-surface-container-lowest)',
  border: 'var(--color-outline-variant)',
  borderStrong: 'var(--color-outline)',
  /** Selected/focused row + avatar-tile background (subtle blue wash). */
  selectedBg: 'var(--color-primary-container)',
  /** Raised/inset fill that must stay visible AGAINST the main surface in both
   *  themes — row hover, small role badges/pills. (Light: subtle grey; dark:
   *  a step above the panel.) */
  surfaceRaised: 'var(--color-surface-container)',
  /** Label text for chips/pills AND bare role labels on the panel surface —
   *  theme-aware so it stays legible on light backgrounds (the chip fills are
   *  low-alpha tints of the panel surface, not opaque dark). */
  chipText: 'color-mix(in srgb, var(--color-on-surface) 80%, transparent)',
  // Brand / saturated accents — theme-independent on purpose.
  primary: '#37A0EF',
  sidebarText: '#ECEFF1',
  // Action/link blue — ghost actions (Edit, Add Association) as TEXT on the
  // panel surface. Theme-aware (`on-primary-container`): a dark blue in light
  // mode (legible on light surfaces) and a light blue in dark mode — unlike the
  // fixed `primary`, which is too low-contrast as text on a light surface.
  accentBlue: 'var(--color-on-primary-container)',
  // Outlined-blue border for ghost blue buttons + the Active status pill.
  accentBlueBorder: 'var(--color-primary)',
  // Active status-pill text — sits on the `selectedBg` (primary-container) pill,
  // so pair it with `on-primary-container` for contrast in both themes.
  activePillText: 'var(--color-on-primary-container)',
  // Text — theme-aware (flips with light/dark; secondary/tertiary derived from
  // the theme's on-surface color to preserve the muted hierarchy in both modes).
  text: 'var(--color-on-surface)',
  textSecondary: 'color-mix(in srgb, var(--color-on-surface) 65%, transparent)',
  textTertiary: 'color-mix(in srgb, var(--color-on-surface) 45%, transparent)',
  // Status — success/danger theme-aware (design-system vars: readable on light
  // AND dark surfaces). `warning` stays a fixed Pencil hex (saturated accent,
  // reads on both surfaces).
  success: 'var(--color-success)',
  warning: '#E0913A',
  danger: 'var(--color-error)',
  // Surface coding — platform-admin = purple (internal), flutter-app = green (tenant).
  chartPurple: '#A47BE0',
} as const;

export type ColorKey = keyof typeof COLORS;

/**
 * Translucent tint of any token color, for chip/pill fills and borders.
 *
 * The Pencil prototype applied alpha by hex-suffix concatenation
 * (`${color}1A`), which silently produces invalid CSS — an unstyled chip —
 * when the token is a `var(--…)` or `color-mix(…)` value (the theme-aware
 * tokens above). ALWAYS tint through this helper; never concatenate.
 */
export function alpha(color: string, percent: number): string {
  return `color-mix(in srgb, ${color} ${percent}%, transparent)`;
}

/**
 * The app's monospace stack.
 *
 * Exported as a token because the bare CSS keyword `monospace` is NOT this: it
 * resolves to whatever the browser ships — a Courier on most — so a permission
 * code rendered with it sits beside one rendered with `Roboto Mono` and the two
 * do not match. That is exactly what happened on the Roles detail, and the only
 * durable fix is for there to be one name to reach for.
 */
export const FONT_MONO = 'Roboto Mono, monospace';
